import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore, useClientStore } from '../../stores'
import { ClientList, ClientForm, type ClientFormData } from '../../components/client'
import { Button, Input, UsageCard } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { seatService, type ClientSummary } from '../../services'
import type { Client, ClientInsert, ClientUpdate } from '../../types/database.types'

export function ClientsPage() {
  const { profile } = useAuthStore()
  const {
    clients,
    isLoading,
    fetchClients,
    createClient,
    updateClient,
    archiveClient,
    sendClientInvitation,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
  } = useClientStore()
  const toast = useToast()

  const [showForm, setShowForm] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [clientUsage, setClientUsage] = useState<ClientSummary | null>(null)
  const [resendingClientId, setResendingClientId] = useState<string | null>(null)

  useEffect(() => {
    if (profile?.id) {
      fetchClients(profile.id)
      // Load client usage
      seatService.getProviderClientSummary(profile.id).then(setClientUsage)
    }
  }, [profile?.id, fetchClients, searchQuery, filters])

  const handleSubmit = async (formData: ClientFormData) => {
    setIsSubmitting(true)
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData.clientData as ClientUpdate)
        toast.success('Client updated successfully')
      } else {
        await createClient({
          clientData: formData.clientData as ClientInsert,
          sendInvitation: formData.sendInvitation,
          providerName: profile?.full_name || 'Your Provider',
        })
        const message = formData.sendInvitation
          ? 'Client added and invitation email sent'
          : 'Client added successfully'
        toast.success(message)
        // Refresh usage after adding client
        if (profile?.id) {
          seatService.getProviderClientSummary(profile.id).then(setClientUsage)
        }
      }
      setShowForm(false)
      setEditingClient(null)
    } catch (error) {
      // Check for limit reached error
      const errorMessage = error instanceof Error ? error.message : 'Failed to save client'
      if (errorMessage.includes('client limit')) {
        toast.error('You have reached your client limit. Please upgrade your plan.')
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleArchive = async (client: Client) => {
    if (!confirm(`Archive ${client.full_name}? They will lose access to assigned programs.`)) {
      return
    }
    try {
      await archiveClient(client.id)
      toast.success('Client archived')
    } catch (error) {
      toast.error('Failed to archive client')
    }
  }

  const handleEdit = (client: Client) => {
    setEditingClient(client)
    setShowForm(true)
  }

  const handleResendInvitation = async (client: Client) => {
    setResendingClientId(client.id)
    try {
      const result = await sendClientInvitation(
        client,
        profile?.full_name || 'Your Provider'
      )
      if (result.success) {
        toast.success(`Invitation sent to ${client.email}`)
      } else {
        toast.error(result.error || 'Failed to send invitation')
      }
    } catch (error) {
      toast.error('Failed to send invitation')
    } finally {
      setResendingClientId(null)
    }
  }

  const canManageClients = profile?.subscription_tier !== 'free'

  if (!canManageClients) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Client Management Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Basic or higher to manage clients and assign programs.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade Subscription</Button>
        </Link>
      </div>
    )
  }

  const isAtClientLimit = clientUsage?.is_at_limit ?? false
  const canAddClient = !isAtClientLimit

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="mt-1 text-gray-600">
            Manage your clients and their program assignments
          </p>
        </div>
        {canAddClient ? (
          <Button onClick={() => setShowForm(true)}>Add Client</Button>
        ) : (
          <Link to="/dashboard/subscription">
            <Button>Upgrade to Add More</Button>
          </Link>
        )}
      </div>

      {/* Client usage card */}
      {clientUsage && clientUsage.max_clients !== 0 && (
        <div className="mb-6">
          <UsageCard
            label="Clients"
            current={clientUsage.current_clients}
            max={clientUsage.max_clients}
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            }
          />
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={
            filters.hasAccount !== undefined
              ? filters.hasAccount ? 'has_account' : 'no_account'
              : filters.status || ''
          }
          onChange={(e) => {
            const value = e.target.value
            // Handle account-based filters
            if (value === 'has_account') {
              setFilters({ status: undefined, hasAccount: true })
            } else if (value === 'no_account') {
              setFilters({ status: undefined, hasAccount: false })
            } else if (value) {
              // Handle status filters
              setFilters({ status: value as Client['status'], hasAccount: undefined })
            } else {
              // Clear all filters
              setFilters({ status: undefined, hasAccount: undefined })
            }
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All clients</option>
          <optgroup label="Account Status">
            <option value="has_account">Has Account</option>
            <option value="no_account">No Account</option>
          </optgroup>
          <optgroup label="Client Status">
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="expired">Expired</option>
            <option value="archived">Archived</option>
          </optgroup>
        </select>
      </div>

      {/* Client list */}
      <ClientList
        clients={clients}
        isLoading={isLoading}
        onEditClient={handleEdit}
        onArchiveClient={handleArchive}
        onResendInvitation={handleResendInvitation}
        resendingClientId={resendingClientId}
        emptyMessage={
          searchQuery
            ? 'No clients match your search'
            : 'No clients yet. Add your first client!'
        }
      />

      {/* Add/Edit form modal */}
      {showForm && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowForm(false)
              setEditingClient(null)
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </h2>
            <ClientForm
              client={editingClient}
              providerId={profile.id}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowForm(false)
                setEditingClient(null)
              }}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}
    </div>
  )
}
